import { useState } from "react";
import { apiPatch } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";

const FREQUENCIES = [
  { value: "daily",   label: "Daily",   desc: "Every 24 hours" },
  { value: "weekly",  label: "Weekly",  desc: "Every 7 days" },
  { value: "monthly", label: "Monthly", desc: "Every 30 days" },
];

export default function EditScheduleModal({ schedule, onClose, onSuccess }) {
  const { showSuccess, showError } = useToast();
  const [label, setLabel] = useState(schedule.label || "");
  const [frequency, setFrequency] = useState(schedule.frequency || "weekly");
  const [enabled, setEnabled] = useState(schedule.enabled ?? true);
  const [notifyEmail, setNotifyEmail] = useState(
    Boolean(schedule.notify_email || schedule.notifyEmail)
  );
  const [webhookUrl, setWebhookUrl] = useState(schedule.webhook_url || schedule.webhookUrl || "");
  const [slackWebhookUrl, setSlackWebhookUrl] = useState(schedule.slack_webhook_url || schedule.slackWebhookUrl || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiPatch(`/api/schedules/${schedule.id}`, {
        label: label.trim(),
        frequency,
        enabled,
        notifyEmail,
        webhookUrl: webhookUrl.trim() || null,
        slackWebhookUrl: slackWebhookUrl.trim() || null,
      });
      showSuccess("Schedule updated successfully.");
      onSuccess(data);
      onClose();
    } catch (err) {
      showError(parseApiError(err, "Failed to update schedule"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-schedule-title"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(560px, 100%)" }}
      >
        <div className="modal-header">
          <h2 id="edit-schedule-title" className="page-subtitle" style={{ margin: 0 }}>
            Edit Schedule
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal-lead" style={{ marginBottom: 20 }}>
          Modify the frequency, status, alert settings, or metadata for this monitored URL.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="input-group" style={{ marginBottom: 12, display: "flex", gap: 12 }}>
            <div style={{ flex: 2 }}>
              <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                Website URL
              </label>
              <input
                className="input"
                type="text"
                value={schedule.url}
                disabled
                style={{ width: "100%", background: "var(--bg)", cursor: "not-allowed" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                Label (optional)
              </label>
              <input
                className="input"
                type="text"
                placeholder="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: 16, display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                Generic Webhook URL
              </label>
              <input
                className="input"
                type="url"
                placeholder="Generic Webhook URL (optional)"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                Slack Webhook URL
              </label>
              <input
                className="input"
                type="url"
                placeholder="Slack Webhook URL (optional)"
                value={slackWebhookUrl}
                onChange={(e) => setSlackWebhookUrl(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 6, display: "block" }}>
              Frequency
            </label>
            <div className="schedule-freq-row" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {FREQUENCIES.map((f) => (
                <label
                  key={f.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    border: `1px solid ${frequency === f.value ? "var(--accent)" : "var(--border)"}`,
                    borderRadius: "var(--radius-sm)",
                    background: frequency === f.value ? "var(--accent-subtle)" : "var(--bg-card)",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    userSelect: "none",
                    flex: 1,
                    minWidth: "100px",
                  }}
                >
                  <input
                    type="radio"
                    name="edit-frequency"
                    value={f.value}
                    checked={frequency === f.value}
                    onChange={() => setFrequency(f.value)}
                    style={{ display: "none" }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{f.label}</div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{f.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: 20, display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", marginBottom: 4, display: "block" }}>
                Status
              </label>
              <select
                className="input"
                value={enabled ? "active" : "paused"}
                onChange={(e) => setEnabled(e.target.value === "active")}
                style={{ width: "100%", height: 40 }}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>

            <div style={{ flex: 1, display: "flex", alignItems: "center", height: "100%", paddingTop: 20 }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--bg-card)",
                  width: "100%",
                  cursor: "pointer",
                  userSelect: "none"
                }}
              >
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                />
                <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Email me when each run completes</span>
              </label>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving Changes..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
